/**
 * DeployBanner
 * ─────────────────────────────────────────────
 * Persistent top-of-page banner for severity='high' deployments.
 * Sits ABOVE the Navbar. Pushes content down (not overlapping).
 * Requires explicit user action — either refresh or dismiss.
 * Synced across tabs via BroadcastChannel in useChangelog.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAlertTriangle, FiRefreshCw, FiX } = FiIcons;

const DeployBanner = ({ show, headline, onRefresh, onDismiss }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{   opacity: 0, height: 0 }}
        transition={{ duration: 0.35, ease: 'easeInOut' }}
        className="fixed top-0 left-0 right-0 z-[700] overflow-hidden"
        style={{
          background: 'linear-gradient(90deg, rgba(255,102,204,0.15) 0%, rgba(255,102,0,0.12) 100%)',
          borderBottom: '1px solid rgba(255,102,204,0.3)',
          backdropFilter: 'blur(12px)',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between gap-4">
          {/* Left */}
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 rounded-lg bg-brand-pink/20 border border-brand-pink/30 flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiAlertTriangle} className="text-brand-pink text-[10px]" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-widest text-brand-pink">
                Breaking Update
              </span>
              <span className="text-[11px] font-bold text-zinc-300 hidden sm:inline">
                {headline || "A critical update was deployed. Please refresh to continue."}
              </span>
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onRefresh}
              className="flex items-center gap-1.5 px-3 py-1.5 btn-brand rounded-xl text-[10px]"
            >
              <SafeIcon icon={FiRefreshCw} className="text-[10px]" />
              Refresh Now
            </button>
            <button
              onClick={onDismiss}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/8 transition-all"
            >
              <SafeIcon icon={FiX} className="text-[10px]" />
            </button>
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default DeployBanner;